import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ThemeToggle } from './ThemeToggle';
import { ArrowLeft, ChevronRight, Search } from 'lucide-react';
import { NotificationsBell } from './notifications/NotificationsBell';
import { NAV } from '@/config/nav';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import type { LabelKey } from '@/lib/crm-pages';

export interface Crumb {
  label: string;
  /** Omit on the last crumb — the current page isn't a link to itself. */
  to?: string;
}

interface Props {
  title: string;
  /**
   * Legacy plain-text trail, e.g. "Audience / Contacts". Segments are split on
   * "/" and the non-final ones are linked when a route can be resolved for
   * them (see SECTION_ROUTES). Pass `crumbs` instead for explicit control.
   */
  breadcrumb?: string;
  /** Explicit, linkable trail. Takes precedence over `breadcrumb`. */
  crumbs?: Crumb[];
  description?: string;
  /** Optional slot rendered to the left of the breadcrumb/title (e.g. a Back button). */
  back?: React.ReactNode;
  /**
   * Auto back-arrow for sub-pages. On by default: any route two or more
   * segments deep gets an arrow to its parent path, so every detail/edit page
   * has a way out without touching each page. Pass false to suppress it, or
   * pass `back` to supply your own control.
   */
  autoBack?: boolean;
  right?: React.ReactNode;
  showSearch?: boolean;
}

/**
 * Known breadcrumb section labels → routes, so the legacy string form becomes
 * clickable without editing ~98 call sites. Unknown labels render as plain
 * text rather than a dead link.
 */
const SECTION_ROUTES: Record<string, string> = {
  'audience': '/contacts',
  'contacts': '/contacts',
  'groups': '/groups',
  'leads': '/leads',
  'conversation hub': '/chats',
  'whatsapp': '/whatsapp',
  'templates': '/templates',
  'campaigns': '/campaigns',
  'facebook ads': '/facebook/campaigns',
  'instagram': '/instagram',
  'tiktok ads': '/tiktok',
  'google ads': '/google-ads/campaigns',
  'ai studio': '/ai-studio',
  'appointments': '/appointments/services',
  'settings': '/settings',
  'deals': '/deals',
  'reports': '/reports',
  'workflows': '/workflows',
  'documents': '/documents',
};

/**
 * Every sidebar destination is a "parent" page. A page that IS one gets no back
 * arrow; a deeper page goes back to the nearest ancestor that is one — which,
 * unlike a blind "drop the last segment", is guaranteed to exist.
 */
const NAV_PATHS: Set<string> = new Set(
  NAV.flatMap((g) => g.items.map((i) => i.to.replace(/\/+$/, '') || '/')),
);
NAV_PATHS.add('/settings');

function parentPageFor(pathname: string): string | null {
  const clean = pathname.replace(/\/+$/, '') || '/';
  if (NAV_PATHS.has(clean)) return null;                     // already a parent page
  const segs = clean.split('/').filter(Boolean);
  for (let i = segs.length - 1; i >= 1; i--) {
    const candidate = `/${segs.slice(0, i).join('/')}`;
    if (NAV_PATHS.has(candidate)) return candidate;
  }
  return null;                                               // no known ancestor → no arrow
}

export const Topbar: React.FC<Props> = ({ title, breadcrumb, crumbs, description, back, autoBack = true, right, showSearch = false }) => (
  <header className="sticky top-0 z-30 border-b border-border/80 bg-surface/90 backdrop-blur-xl">
    {/* Stack vertically on mobile so the right-rail action buttons can wrap
        without overlapping the title. flex-col below `sm`, side-by-side from
        `sm` onwards (≥640px). */}
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-6 px-4 sm:px-7 py-3 sm:py-4">
      <div className="flex items-center gap-3 min-w-0">
        {back ?? <AutoBack enabled={autoBack} />}
        <div className="min-w-0">
          <Breadcrumbs crumbs={crumbs} breadcrumb={breadcrumb} />
          <h1 className="mt-0.5 font-display text-[18px] sm:text-[20px] font-semibold tracking-tight text-text-primary leading-tight">
            {title}
          </h1>
          {description && (
            <p className="mt-0.5 truncate text-[12px] sm:text-[12.5px] text-text-muted">{description}</p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        {showSearch && (
          <div className="relative hidden md:block">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search..."
              className="h-9 w-60 rounded-lg border border-border bg-surface-alt pl-9 pr-3 text-[13px] text-text-primary placeholder:text-text-muted focus:border-primary focus:bg-surface focus:outline-none focus:ring-4 focus:ring-primary-soft transition"
            />
          </div>
        )}
        {right}
        <NotificationsBell />
        <ThemeToggle />
      </div>
    </div>
  </header>
);

const AutoBack: React.FC<{ enabled: boolean }> = ({ enabled }) => {
  const nav = useNavigate();
  const { pathname } = useLocation();
  if (!enabled) return null;
  const parent = parentPageFor(pathname);
  const depth = pathname.split('/').filter(Boolean).length;
  const isParentPage = NAV_PATHS.has(pathname.replace(/\/+$/, '') || '/');
  if (isParentPage || depth < 2) return null;
  return (
    <button
      onClick={() => (parent ? nav(parent) : nav(-1))}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
      aria-label="Back"
      title="Back"
    >
      <ArrowLeft size={16} />
    </button>
  );
};

/** Renamed sections (Contacts → Guests) still link to their pages. */
const LABEL_ROUTES: Array<[LabelKey, string]> = [
  ['leads', '/leads'], ['contacts', '/contacts'], ['deals', '/deals'], ['appointments', '/appointments'],
  ['services', '/appointments/services'], ['bookings', '/appointments/bookings'],
];

const Breadcrumbs: React.FC<{ crumbs?: Crumb[]; breadcrumb?: string }> = ({ crumbs, breadcrumb }) => {
  const { labels } = useWorkspace();
  const routeFor = (label: string): string | undefined => {
    const k = label.toLowerCase();
    return LABEL_ROUTES.find(([key]) => labels[key]?.plural.toLowerCase() === k)?.[1] ?? SECTION_ROUTES[k];
  };
  const resolved: Crumb[] = crumbs
    ?? (breadcrumb
      ? breadcrumb.split('/').map((raw, i, arr) => {
        const label = raw.trim();
        const to = i === arr.length - 1 ? undefined : routeFor(label);
        return { label, to };
      })
      : []);

  if (resolved.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-2xs font-medium uppercase tracking-[0.14em] text-text-muted">
      {resolved.map((c, i) => (
        <span key={`${c.label}-${i}`} className="flex items-center gap-1">
          {i > 0 && <ChevronRight size={10} className="opacity-60" aria-hidden />}
          {c.to ? (
            <Link to={c.to} className="transition-colors hover:text-primary hover:underline">{c.label}</Link>
          ) : (
            <span>{c.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
};
